// PALLADIUM LMS - COMPLETE COURSE CURRICULUM
// All 4 courses with full lessons, content, and quizzes

export const courseCurriculum = [
  {
    id: 'hipaa',
    title: 'HIPAA for FQHCs',
    description: 'Privacy, Security, and Breach Notification. Complete course with 8 lessons covering the Privacy Rule, Security Rule, 42 CFR Part 2, and breach response.',
    duration: '1h 45m',
    lessons: [
      {
        id: 'hip-1',
        title: 'What is HIPAA and Why It Matters for Your FQHC',
        content: `HIPAA was passed in 1996 as a law to protect the privacy and security of health information. Every FQHC that receives federal funding must comply with HIPAA — it's not optional.

During an HRSA site visit, surveyors will ask about your HIPAA policies, training, and incidents.

If your FQHC has a HIPAA breach, the consequences can be severe:
- Loss of federal funding
- Fines up to $50,000 per violation
- Damage to the clinic's reputation

KEY POINT: Your job touches patient information. HIPAA is your responsibility.

HIPAA = Health Insurance Portability and Accountability Act. It has three main rules:
1. The Privacy Rule — Controls how your clinic uses and shares patient information
2. The Security Rule — Sets standards for protecting electronic patient information
3. The Breach Notification Rule — Requires notification if patient data is lost or disclosed improperly

WHY DOES YOUR FQHC HAVE TO FOLLOW HIPAA?
FQHCs receive federal funding to serve underserved populations. That funding comes with conditions. HIPAA compliance is one of them. During an operational site visit (every 3 years), HRSA surveyors will review your HIPAA policies, training records, and breach response procedures.

THE BIG RULE:
You must minimize access to patient information to the minimum necessary for your job. If you don't need to see someone's mental health history to schedule them, you don't ask for it.`,
        duration: '15 min',
        quiz: [
          {
            question: 'What does HIPAA stand for?',
            options: [
              'Health Insurance Portability and Accountability Act',
              'Health Information Privacy and Access Act',
              'Healthcare Insurance and Patient Authorization Act'
            ],
            correct: 0
          },
          {
            question: 'During an HRSA site visit, surveyors will ask about your FQHC\'s:',
            options: [
              'HIPAA policies and training only',
              'HIPAA policies, training, and breach response procedures',
              'Patient satisfaction scores'
            ],
            correct: 1
          }
        ]
      },
      {
        id: 'hip-2',
        title: 'Protected Health Information (PHI) — What Counts',
        content: `Protected Health Information (PHI) is any information in a medical record or health plan that can identify a patient. HIPAA protects it.

EXAMPLES OF PHI:
- Name
- Medical record number
- Birth date
- Address
- Phone number
- Email
- Social Security number
- Insurance member ID
- Diagnosis
- Medication list
- Test results
- Mental health information

WHEN INFORMATION BECOMES PHI:
A patient's name + any health fact = PHI
A patient identifier (MRN, SSN) + any health fact = PHI
A patient's address + any mental health information = PHI

WHEN INFORMATION IS NOT PHI:
If information has been properly de-identified, it's NOT PHI and can be used more freely.
De-identified means: All identifiers removed (name, MRN, date of birth, address, phone, email, SSN)

PHI AT YOUR FQHC:
You handle PHI in multiple formats:
- Paper: Medical records, sign-in sheets, appointment books
- Electronic: EHR systems, emails, text messages, spreadsheets
- Verbal: Conversations about patients, phone calls

All of it is PHI and all of it must be protected.

YOUR RESPONSIBILITY:
Recognize PHI. Before you handle, store, send, or discuss any information, ask: "Could this identify a patient, and does it relate to their health?" If yes, it's PHI and you must protect it.`,
        duration: '12 min',
        quiz: [
          {
            question: 'Which of the following is PHI?',
            options: [
              'Patient is 45 years old',
              'Patient is 45 years old with hypertension',
              'John Smith is 45 years old with hypertension',
              'Both B and C'
            ],
            correct: 3
          }
        ]
      },
      {
        id: 'hip-3',
        title: 'The Privacy Rule — Using and Disclosing Patient Information',
        content: `The Privacy Rule controls how your FQHC can use and disclose (share) Protected Health Information.

PERMITTED USES FOR TREATMENT, PAYMENT, AND OPERATIONS (TPO):
Your FQHC can use and disclose PHI without patient permission for:

Treatment: Providing, coordinating, or managing healthcare. Sharing an allergy list with the clinician before a visit.

Payment: Billing, insurance submission, collecting copays. Sending diagnosis and service codes to insurance.

Healthcare Operations: Activities that keep your clinic running — quality improvement, staff training, compliance. Using de-identified data in training.

KEY POINT: Even for TPO, you use and disclose only the minimum necessary information.

EXAMPLES OF PERMITTED DISCLOSURES (TPO):
- Insurance: Sending claims to insurance companies
- Referrals: Sending medical records to a specialist
- Follow-up: Calling a patient to remind them of an appointment
- Lab results: Sharing results with the lab
- Quality improvement: Using de-identified data to review outcomes
- Training: Teaching staff using de-identified examples

DISCLOSURES REQUIRING WRITTEN PERMISSION:
Your FQHC cannot disclose PHI without written authorization for:
- Marketing
- Selling patient information
- Psychotherapy notes
- Most research

SPECIAL RULES FOR SENSITIVE INFORMATION:
Mental Health: Requires special consent in many states
Substance Use Disorder (42 CFR Part 2): Federal law stricter than HIPAA
HIV/AIDS Status: Protected in some states

THE MINIMUM NECESSARY STANDARD:
Even when you have permission, share only what's needed. Don't send the entire chart when only specific information is relevant.`,
        duration: '15 min',
        quiz: [
          {
            question: 'What does TPO stand for?',
            options: [
              'Treatment, Payment, Operations',
              'Treatment, Prescriptions, Oversight',
              'Therapy, Payments, Outcomes'
            ],
            correct: 0
          }
        ]
      },
      {
        id: 'hip-4',
        title: 'The Security Rule — Protecting Electronic PHI',
        content: `The Security Rule requires how you must protect electronic PHI (ePHI) — patient information stored or transmitted using computers, networks, or the internet.

THREE MAIN CATEGORIES OF SAFEGUARDS:

1. ADMINISTRATIVE SAFEGUARDS
- Policies & Procedures: Written policies about who can access ePHI
- Workforce Security: Only people who need ePHI for their job get access
- Authorization & Supervision: Appropriate access levels
- Termination Procedures: Remove access immediately when someone leaves
- Training: All staff must be trained on security
- Incident Response: Your clinic has a plan for breaches

2. PHYSICAL SAFEGUARDS
- Server & Computer Security: Servers are in a locked room
- Workstation Use: Clinic computers are for work only
- Workstation Security: Password-protected, automatic logout
- Media Handling: Secure printed documents, shred when done
- Facility Access: Building is locked, visitors don't wander

3. TECHNICAL SAFEGUARDS
- Encryption: Patient information must be scrambled
- Access Controls: Only authorized people can access specific data
- Audit Logs: System records who accessed what, when, why
- Firewalls & Intrusion Detection: Prevents unauthorized access
- Malware Protection: Antivirus software prevents infection
- Software Updates: Keep systems current

RECENT CHANGES (2024–2025):
Starting in 2026, all organizations must implement:
- Multi-factor authentication (MFA): Password + something else
- Encryption: Patient information must be scrambled
- Automatic logout: Computers log you out after idle time
- Vulnerability scanning: Check for weaknesses twice a year
- Penetration testing: Security firm tries to break in
- 72-hour breach notification: Notify individuals within 72 hours

YOUR ROLE IN SECURITY:
1. Use strong passwords (not "password123")
2. Don't share logins
3. Lock your computer when you step away
4. Report suspicious activity immediately
5. Don't use public WiFi for clinic work
6. Be careful with suspicious emails (phishing)
7. Shred or securely dispose of paper with PHI
8. Keep portable devices secure`,
        duration: '18 min',
        quiz: [
          {
            question: 'Which of the following is a technical safeguard?',
            options: [
              'A written policy about who can access PHI',
              'Locking the server room door',
              'Encryption of patient information on computers',
              'Annual staff training'
            ],
            correct: 2
          },
          {
            question: 'Starting in 2026, HIPAA requires:',
            options: [
              'Passwords only',
              'Multi-factor authentication (MFA)',
              'Fingerprint scanning only'
            ],
            correct: 1
          }
        ]
      },
      {
        id: 'hip-5',
        title: 'Breach Notification & What Happens When Things Go Wrong',
        content: `A breach is the unauthorized access, acquisition, use, or disclosure of Protected Health Information.

A breach does NOT include:
- Someone accidentally opening the wrong chart but not doing anything
- Someone accessing a chart they were authorized to access
- Losing encrypted information that is immediately recovered

A breach DOES include:
- Someone intentionally stealing patient records
- An employee accessing charts out of curiosity
- A hacker breaking into your system
- Patient information left on a USB drive found in public
- An unencrypted laptop stolen from a car
- An email sent to the wrong clinic with patient data

WHAT YOUR FQHC MUST DO AFTER A BREACH:

1. Stop the breach immediately
2. Assess the harm (did information actually get compromised?)
3. Notify affected individuals without unreasonable delay
4. Notify the media (if more than 500 people affected)
5. Notify the federal government (HHS)
6. Document everything
7. Correct the problem and prevent future breaches

CONSEQUENCES OF A BREACH:

For the patient:
- Risk of identity theft or fraud
- Loss of privacy
- Stress and concern

For your FQHC:
- OCR investigation by the Office for Civil Rights
- Fines: $100 to $50,000 per violation
- Corrective action plans
- Reputational damage
- Loss of federal funding
- Criminal charges in severe cases

If you discover or suspect a breach:
1. Report it immediately to your Privacy Officer or manager
2. Don't try to handle it yourself
3. Cooperate with the investigation
4. Follow the clinic's incident procedures
5. Keep it confidential`,
        duration: '12 min',
        quiz: [
          {
            question: 'A breach is:',
            options: [
              'Any loss of patient information',
              'Unauthorized access, use, or disclosure of PHI',
              'Any accidental opening of a chart'
            ],
            correct: 1
          }
        ]
      },
      {
        id: 'hip-6',
        title: 'Special Rules — 42 CFR Part 2 for Behavioral Health',
        content: `42 CFR Part 2 is a federal law that protects substance use disorder (SUD) and alcohol treatment records. It's STRICTER than HIPAA.

THE BIG RULE:
You cannot disclose 42 CFR Part 2 records to anyone without the patient's written consent — with very limited exceptions. This applies even to:
- Other providers at your own clinic without direct care role
- Insurance companies
- Law enforcement (with limited exceptions)
- Family members

WHAT'S PROTECTED:
- The fact that someone is in SUD treatment
- Diagnoses and treatment plans related to substance use
- Test results for drugs or alcohol
- Clinical progress notes
- Billing and payment information tied to SUD records
- Names and identifying information if it reveals SUD treatment

WHEN YOU CAN DISCLOSE (Exceptions):
1. With written consent (specific 42 CFR Part 2 form)
2. Medical emergency (limited disclosure)
3. Reporting child abuse or neglect
4. Court order (but still limited)
5. Audit or evaluation (de-identified only)

THE CONSENT FORM:
A 42 CFR Part 2 consent is DIFFERENT from HIPAA authorization. It must include:
- Specific name/organization getting the information
- What information will be disclosed
- Patient signature and date
- Expiration date (12 months if not specified)
- Statement that consent can be revoked

YOUR FQHC'S RESPONSIBILITY:
- Separate SUD records if you provide treatment
- Restrict access to only those providing care
- Train all staff (not just clinical)
- Document consents securely
- Audit access regularly
- Have a written policy

Remember: Even if you have a legitimate reason to access SUD records, you need the specific written consent first.`,
        duration: '10 min',
        quiz: [
          {
            question: '42 CFR Part 2 is:',
            options: [
              'Less strict than HIPAA',
              'The same as HIPAA',
              'Stricter than HIPAA for substance use disorder records'
            ],
            correct: 2
          }
        ]
      },
      {
        id: 'hip-7',
        title: 'Your Role — Privacy in Your Job',
        content: `You work at an FQHC. No matter your role, you handle patient information and must follow HIPAA and 42 CFR Part 2.

BY ROLE:

Clinical Staff (MA, RN, PA, MD, therapist):
- See patient information to provide care
- Follow the minimum necessary standard
- Don't share diagnosis or treatment details outside patient care
- Handle paper charts securely
- Use EHR access appropriately
- Know when to ask about consents (especially 42 CFR Part 2)

Front Desk & Patient Access:
- Collect patient information securely
- See insurance and demographic info
- Don't access clinical charts unless needed for your job
- Maintain privacy during check-in
- Don't discuss patient information in public
- Handle printed materials securely

Billing & Revenue Cycle:
- Access diagnosis and procedure information for claims
- Handle insurance and financial information
- Use minimum necessary — don't pull full charts if you only need codes
- Secure billing documents
- Never discuss a patient's insurance or balance with anyone else
- Know when 42 CFR Part 2 rules apply

YOUR SPECIFIC RESPONSIBILITIES:

General:
- Know your clinic's HIPAA and 42 CFR Part 2 policies
- Protect patient information as you would your own
- Ask questions if you're unsure
- Report concerns or breaches immediately

Handling Patient Information:
- Use it only for your job
- Share it only with people who need it for care, payment, or operations
- De-identify it if using for training or analysis
- Encrypt it if taking it out of the building
- Lock your screen when you step away
- Use strong passwords
- Log out at the end of your shift

In Public:
- Don't discuss patients in hallways or break rooms
- Don't use patient names in conversations
- Don't post about patients on social media
- Don't share patient info with friends or family

Technology:
- Don't share your login
- Don't access charts you're not supposed to
- Don't take photos of records
- Don't email patient information unless it's secure
- Don't use public WiFi for patient work

WHO IS YOUR PRIVACY OFFICER?
Find out. If you have questions or concerns, go to them.`,
        duration: '12 min',
        quiz: [
          {
            question: 'Your role in HIPAA compliance is:',
            options: [
              'Only the Privacy Officer\'s responsibility',
              'A responsibility of everyone at the clinic',
              'Only for people who directly access charts'
            ],
            correct: 1
          }
        ]
      },
      {
        id: 'hip-8',
        title: 'Course Review & Final Assessment',
        content: `KEY TAKEAWAYS:

HIPAA PROTECTS PATIENT PRIVACY AND SECURITY
- HIPAA applies to all FQHC staff
- It's not optional — it's a condition of federal funding
- Violations carry heavy penalties

PHI IS ANY INFORMATION THAT IDENTIFIES A PATIENT + HEALTH INFORMATION
- Names, MRN, addresses, dates of birth, diagnoses, medications — all PHI
- De-identified information is no longer PHI
- You're responsible for recognizing and protecting PHI

THE PRIVACY RULE CONTROLS HOW YOU USE AND DISCLOSE PATIENT INFORMATION
- Use and disclose only for treatment, payment, and operations (TPO)
- Follow the minimum necessary standard
- Some information requires special consent
- Some disclosures require written authorization

THE SECURITY RULE REQUIRES SAFEGUARDING ELECTRONIC PHI
- Administrative safeguards: policies, training, workforce security
- Physical safeguards: locking rooms, secure workstations, document handling
- Technical safeguards: encryption, access controls, software updates
- As of 2026, multi-factor authentication is required

BREACHES MUST BE REPORTED
- Notify patients, the government, and media (if large enough)
- Document everything
- This is where reputational and financial damage happens

42 CFR Part 2 IS STRICTER THAN HIPAA
- If your FQHC provides SUD treatment, strict rules apply
- Cannot disclose without written 42 CFR Part 2 consent
- Even to other providers at your own clinic without consent

YOU ARE RESPONSIBLE
- You handle patient information in your job
- You must know the rules and follow them
- Report concerns and breaches immediately
- When in doubt, ask your Privacy Officer

You have completed HIPAA for FQHCs training. Congratulations!

Your Certificate of Completion verifies you have received and understood HIPAA training required by your FQHC and by HRSA.`,
        duration: '15 min',
        quiz: [
          {
            question: 'HIPAA applies to:',
            options: [
              'Only the Privacy Officer',
              'Clinical staff only',
              'All FQHC staff',
              'Only staff who access medical records'
            ],
            correct: 2
          },
          {
            question: 'The minimum necessary standard means:',
            options: [
              'Limit disclosure to information needed for the specific purpose',
              'You can share the full chart for TPO purposes',
              'Minimize the number of people who see information',
              'Share information but use fake names'
            ],
            correct: 0
          }
        ]
      }
    ]
  },
  {
    id: 'hrsa',
    title: 'HRSA Compliance & Site-Visit Readiness',
    description: 'The 19 program requirements HRSA audits, documentation requirements, and site-visit preparation. Learn what an operational site visit is and how to prepare.',
    duration: '2h',
    lessons: [
      {
        id: 'hrs-1',
        title: 'What is HRSA and the Health Center Program',
        content: `HRSA = Health Resources and Services Administration. Part of the U.S. Department of Health and Human Services.

HRSA funds and oversees FQHCs — Federally Qualified Health Centers — which serve medically underserved populations. Your clinic receives federal funding through HRSA because it serves people who might otherwise go without care.

THE HEALTH CENTER PROGRAM:
The Health Center Program provides grants to clinics that serve:
- Low-income patients
- Uninsured patients
- Homeless individuals
- Migrant and seasonal farmworkers
- Public housing residents
- Other vulnerable populations

WHAT COMES WITH THAT FUNDING:
Access to federal dollars comes with obligations. Your FQHC must:
- Serve all patients regardless of ability to pay (sliding fee scale)
- Be governed by a patient-majority board
- Provide comprehensive primary care
- Meet all compliance requirements

THE OPERATIONAL SITE VISIT:
Every FQHC is reviewed by HRSA surveyors every 3 years. This is called an Operational Site Visit or OSV. Surveyors arrive unannounced or with short notice and spend 3-5 days reviewing:
- Patient records
- Board minutes
- Compliance documentation
- Financial records
- Staff qualifications
- Physical plant and equipment
- Quality assurance activities
- Community outreach and partnerships

WHY THIS MATTERS FOR YOU:
A successful site visit means your clinic keeps its federal funding, which funds the care you provide. A failed visit means corrective action plans, possible funding suspension, and major operational disruption.

THE 19 CORE PROGRAM REQUIREMENTS:
HRSA evaluates your clinic on 19 program requirements. We'll cover these in detail in the next lessons. They include:
1. Needs assessment and community health planning
2. Governance and decision-making
3. Management and operations
4. Services and facilities
5. Quality improvement and clinical care
6. Patient rights and community accountability
7. Financial management
8. Staffing and workforce
9. Continuity of care

Your job is to understand which requirements affect your role and be ready to talk about them if asked by a surveyor.`,
        duration: '18 min',
        quiz: [
          {
            question: 'HRSA stands for:',
            options: [
              'Health Resources and Services Administration',
              'Health Review and Screening Agency',
              'Healthcare Resource and Security Authority'
            ],
            correct: 0
          },
          {
            question: 'An Operational Site Visit occurs every:',
            options: [
              'Year',
              '3 years',
              '5 years',
              'As needed'
            ],
            correct: 1
          }
        ]
      },
      {
        id: 'hrs-2',
        title: 'The 19 Program Requirements & What Surveyors Look For',
        content: `HRSA evaluates FQHCs on 19 specific program requirements. Here's what you need to know about each:

REQUIREMENT 1: COMMUNITY HEALTH NEEDS ASSESSMENT
Your clinic must assess the health needs of the community it serves.
Surveyors look for: A written needs assessment updated regularly. Evidence of community input.
Your role: You may participate in surveys, focus groups, or provide feedback about patient needs.

REQUIREMENT 2: GOVERNANCE & DECISION-MAKING
Your clinic has a Board of Directors that includes patients and staff. They make major decisions.
Surveyors look for: Board minutes showing active governance. Patient majority on the board. Board meetings at least quarterly.
Your role: Know who's on your board. Understand your clinic's governance structure.

REQUIREMENT 3: ORGANIZATION & MANAGEMENT
Your clinic has clear organizational structure, policies, and a management team.
Surveyors look for: Organizational chart. Written policies. Evidence of management oversight.
Your role: Know your reporting structure and where to go with questions or concerns.

REQUIREMENT 4-8: SERVICES AND FACILITIES
Your clinic provides comprehensive primary care services (medical, dental, mental health, pharmacy, etc.).
Surveyors look for: Service availability. Clinic hours. Staff credentials. Clean, safe facilities.
Your role: Ensure your area is organized, equipment works, and services are delivered.

REQUIREMENT 9: CONTINUITY OF CARE
Patients receive follow-up care and coordination between providers.
Surveyors look for: Evidence of referrals, care plans, communication between providers.
Your role: Document follow-up activities in the medical record. Complete referrals on time.

REQUIREMENT 10: QUALITY IMPROVEMENT & CLINICAL PERFORMANCE
Your clinic monitors quality, tracks outcomes, and improves based on data.
Surveyors look for: Quality improvement plans. Clinical data. Evidence of staff training.
Your role: Participate in quality improvement activities. Follow best practices.

REQUIREMENT 11: PATIENT RIGHTS & RESPONSIBILITIES
Patients have rights, including privacy, complaint resolution, and informed consent.
Surveyors look for: Posted patient rights. Evidence of informed consent. Complaint resolution documentation.
Your role: Treat patients with respect. Protect their privacy. Help resolve complaints.

REQUIREMENT 12: COMMUNITY ACCOUNTABILITY & HEALTH PROMOTION
Your clinic is accountable to the community it serves and promotes health.
Surveyors look for: Community partnerships. Health promotion activities. Patient feedback mechanisms.
Your role: Participate in community health activities. Treat the community with respect.

REQUIREMENT 13: MANAGEMENT OF PATIENT RECORDS
Patient records are organized, secure, and accessible.
Surveyors look for: Complete records. Proper documentation. Security and privacy measures.
Your role: Document thoroughly and accurately. Keep records secure.

REQUIREMENT 14: MEDICAL RECORD CONTENT
Medical records contain all required information for quality care and compliance.
Surveyors look for: Complete documentation. Proper signatures. Evidence-based care.
Your role: Document all patient interactions, treatments, and outcomes.

REQUIREMENT 15: FINANCIAL MANAGEMENT
Your clinic manages money responsibly and provides care regardless of ability to pay.
Surveyors look for: Audited financial statements. Evidence of sliding fee scale. Proper billing.
Your role: Understand your clinic's billing and payment policies.

REQUIREMENT 16: INFECTION CONTROL & SAFETY
Your clinic prevents infections and maintains a safe environment.
Surveyors look for: OSHA compliance. Infection control procedures. Safety training.
Your role: Follow infection control procedures. Report safety concerns.

REQUIREMENT 17: WORKFORCE COMPETENCY & PROFESSIONAL DEVELOPMENT
Staff are qualified, trained, and competent.
Surveyors look for: Staff qualifications. Training records. Performance evaluations.
Your role: Maintain current certifications. Participate in training and professional development.

REQUIREMENT 18: CULTURAL & LINGUISTIC COMPETENCE
Your clinic serves diverse populations with respect for culture and language.
Surveyors look for: Bilingual staff. Translated materials. Evidence of cultural competency.
Your role: Respect cultural differences. Use translation services appropriately.

REQUIREMENT 19: HEALTH INFORMATION EXCHANGE
Your clinic shares patient information appropriately with other providers for coordinated care.
Surveyors look for: Evidence of information sharing. Interoperability with other systems.
Your role: Facilitate appropriate sharing of patient information.

COMMON DEFICIENCIES:
Surveyors frequently find:
- Incomplete or late medical record documentation
- Missing informed consent forms
- Staff without current certifications or training
- No evidence of quality improvement activities
- Infection control violations
- Inadequate staffing or credentialing
- Lack of compliance training

HOW TO AVOID DEFICIENCIES:
- Document everything thoroughly
- Keep training records current
- Follow all procedures
- Report issues immediately
- Participate in quality improvement
- Ask your manager if you're unsure`,
        duration: '25 min',
        quiz: [
          {
            question: 'How many core program requirements does HRSA evaluate FQHCs on?',
            options: [
              '10',
              '15',
              '19',
              '25'
            ],
            correct: 2
          },
          {
            question: 'The Board of Directors at an FQHC must include:',
            options: [
              'Mostly clinic staff',
              'Mostly physicians',
              'A patient majority',
              'Only management'
            ],
            correct: 2
          }
        ]
      },
      {
        id: 'hrs-3',
        title: 'Site-Visit Preparation & What to Expect',
        content: `An Operational Site Visit is when HRSA surveyors come to your clinic to verify compliance with the 19 program requirements. Here's what to expect and how to prepare.

WHEN DOES A SITE VISIT HAPPEN?
- Every 3 years as part of routine monitoring
- After a complaint or concern is filed
- When a clinic is first applying for FQHC status
- Unannounced or with 2-4 weeks' notice

WHO COMES?
A team of 2-4 HRSA surveyors with expertise in:
- Healthcare operations
- Compliance and regulation
- Financial management
- Quality and clinical care

HOW LONG DOES IT TAKE?
Usually 3-5 days on-site, plus months of document review before and after.

WHAT SURVEYORS DO:
- Review patient records
- Interview staff and patients
- Tour facilities
- Review board minutes and policies
- Check staff qualifications
- Verify quality improvement activities
- Observe clinic operations
- Check compliance documentation

WHAT THEY'RE LOOKING FOR:
Evidence that your clinic:
- Serves all patients regardless of ability to pay
- Provides comprehensive, quality care
- Follows all regulations (HIPAA, OSHA, etc.)
- Maintains clean, safe facilities
- Employs qualified staff
- Documents everything properly
- Improves quality continuously
- Treats patients and staff with respect

PREPARATION BEFORE THE VISIT:

1-3 Months Before:
- Notify all staff that a visit is coming
- Schedule training on HRSA requirements
- Review your clinic's policies and procedures
- Conduct an internal audit of compliance
- Update any outdated documentation
- Organize board minutes and financial records
- Create an index of where documents are located

1-2 Weeks Before:
- Brief leadership on expectations
- Clean facilities thoroughly
- Prepare a conference room for surveyor meetings
- Gather all required documents and organize them
- Brief front desk staff on what to expect
- Review your clinic's policies with relevant staff
- Ensure all staff are present (if possible)

During the Visit:

- Be professional and courteous
- Answer questions honestly
- If you don't know something, say so — don't guess
- Offer to get the right person if asked
- Follow your normal routine
- Be respectful of surveyor time
- Don't over-explain or be defensive

COMMON QUESTIONS SURVEYORS ASK STAFF:

"What are your clinic's hours?"
"What should a patient do if they have a complaint?"
"How is patient information kept private?"
"What training have you received this year?"
"What would you do if a patient couldn't pay?"
"How do you report safety concerns?"
"Who is your patient population?"
"What is our clinic's mission?"

AFTER THE VISIT:
- Surveyors prepare a report (takes 2-4 weeks)
- You receive findings (either compliant or deficiencies noted)
- If deficiencies exist, you have 30 days to develop a corrective action plan
- You implement the plan and report back to HRSA

WHAT HAPPENS WITH DEFICIENCIES?
- Minor: Usually corrected within 30 days
- Moderate: May require oversight or re-audit
- Serious: Could result in funding suspension
- Very serious: Could result in loss of FQHC status

HOW YOU CAN HELP:
- Know your job responsibilities
- Document everything thoroughly
- Follow procedures and policies
- Keep your area organized and safe
- Be friendly and professional with patients
- Report concerns immediately
- Participate in training and quality improvement

YOUR ROLE IF A SURVEYOR ASKS YOU QUESTIONS:
- Take it seriously — this is important
- Answer honestly
- Be brief and to the point
- If asked about a patient, follow HIPAA (don't share details)
- If asked about policy, be accurate
- Offer to get your manager if you're unsure`,
        duration: '20 min',
        quiz: [
          {
            question: 'An Operational Site Visit typically lasts:',
            options: [
              '1 day',
              '3-5 days',
              '2 weeks',
              '1 month'
            ],
            correct: 1
          },
          {
            question: 'If deficiencies are found during a site visit, your clinic has how long to develop a corrective action plan?',
            options: [
              '7 days',
              '30 days',
              '60 days',
              '90 days'
            ],
            correct: 1
          }
        ]
      },
      {
        id: 'hrs-4',
        title: 'Your Role in HRSA Compliance',
        content: `Every person at your FQHC contributes to compliance with HRSA requirements. Here's your specific role.

BY JOB ROLE:

CLINICAL STAFF (MA, RN, PA, MD, therapist, dental):
Your responsibilities:
- Document patient visits thoroughly and accurately
- Follow clinical protocols and best practices
- Maintain current certifications and licenses
- Keep patient information private
- Report safety concerns
- Participate in quality improvement
- Treat all patients with respect regardless of ability to pay
- Refer appropriately and coordinate care

FRONT DESK & PATIENT ACCESS:
Your responsibilities:
- Verify insurance and demonstrate sliding fee scale
- Enroll patients promptly
- Maintain patient privacy
- Treat all patients with respect
- Handle complaints professionally
- Keep appointment schedules accurate
- Ensure waiting areas are clean and organized
- Report staffing or equipment issues

BILLING & ADMINISTRATIVE:
Your responsibilities:
- Ensure accurate coding and billing
- Verify ability to pay and apply sliding fees appropriately
- Keep financial records organized and secure
- Understand FQHC payment policies
- Report billing discrepancies
- Maintain confidentiality of financial information

MANAGEMENT & LEADERSHIP:
Your responsibilities:
- Ensure staff are trained and competent
- Verify professional qualifications
- Maintain compliance documentation
- Develop and update policies and procedures
- Conduct internal compliance audits
- Plan and conduct quality improvement activities
- Ensure facilities are clean and safe
- Prepare for HRSA site visits

EVERYONE:
- Know your clinic's mission and values
- Understand the 19 HRSA program requirements (especially those relevant to your role)
- Document thoroughly
- Follow all policies and procedures
- Respect patient confidentiality and privacy
- Treat patients and colleagues with dignity
- Report concerns immediately to management
- Ask for clarification if unsure about requirements

WHAT SURVEYORS WANT TO SEE:
- Staff who understand FQHC mission
- Well-documented patient care
- Clean, organized facilities
- Professional, respectful interactions
- Compliance with all regulations
- Evidence of training and competency
- Teamwork and communication
- Commitment to quality and safety

DOCUMENTATION TIPS:
Everything matters during a site visit. Documentation should:
- Be complete and accurate
- Include dates, times, and signatures
- Reflect the care that was provided
- Address all required elements
- Be legible and professional
- Show evidence of informed consent
- Document follow-up and coordination of care
- Demonstrate clinical reasoning

IF A SURVEYOR ASKS YOU:
- Listen carefully to the question
- Answer honestly and directly
- Keep answers brief
- If you don't know, say so
- Offer to get the right person
- Don't make excuses or blame others
- Don't offer extra information not asked for
- Be respectful and professional

YOUR CLINIC'S SLIDING FEE SCALE:
HRSA requires that FQHCs serve patients regardless of ability to pay. You need to:
- Know your clinic's sliding fee policy
- Apply it correctly and fairly
- Never deny care due to inability to pay
- Explain the policy respectfully
- Document fee adjustments
- Treat all patients with the same respect

COMMON MISTAKES TO AVOID:
- Incomplete documentation
- Unclear handwriting
- Missing signatures or dates
- Not following procedures
- Judging patients based on ability to pay
- Discussing patient information in public
- Not keeping up with training
- Failing to report issues
- Treating some patients differently than others

REMEMBER:
HRSA funding allows your clinic to serve patients who might otherwise go without care. That's why compliance is important. Every time you document carefully, protect privacy, or treat a patient with respect, you're protecting that funding and your clinic's mission.`,
        duration: '18 min',
        quiz: [
          {
            question: 'What is the primary reason HRSA requires FQHCs to have a sliding fee scale?',
            options: [
              'To reduce costs for the clinic',
              'To serve patients regardless of ability to pay',
              'To favor low-income patients',
              'To avoid paying taxes'
            ],
            correct: 1
          },
          {
            question: 'If a surveyor asks you a question and you don\'t know the answer, you should:',
            options: [
              'Make up an answer',
              'Say "I don\'t know" and offer to get the right person',
              'Change the subject',
              'Tell them to ask someone else'
            ],
            correct: 1
          }
        ]
      }
    ]
  },
  {
    id: 'fwa',
    title: 'Fraud, Waste & Abuse / Code of Conduct',
    description: 'False Claims Act, OIG expectations, anti-kickback basics, and your clinic\'s code of conduct. Understand what compliance means and how to report concerns.',
    duration: '55 min',
    lessons: [
      {
        id: 'fwa-1',
        title: 'What is Fraud, Waste & Abuse?',
        content: `Your clinic receives federal funding from HRSA, Medicare, Medicaid, and other government programs. With that funding comes an obligation: you must use the money appropriately and honestly.

Fraud, waste, and abuse are three different problems:

FRAUD:
Fraud is intentional deception or misrepresentation to obtain federal funding.

Examples of fraud:
- Billing for services not provided
- Billing a patient twice for the same visit
- Submitting false credentials (claiming a staff member is a licensed provider when they're not)
- Falsifying timesheets
- Billing for procedures that weren't necessary
- Submitting duplicate claims
- Upcoding (billing for a more expensive visit than what actually happened)

Consequences of fraud:
- Federal investigation by OIG (Office of Inspector General)
- Criminal charges and prison time
- Financial penalties (treble damages — 3x what was fraudulently obtained)
- Loss of ability to participate in federal programs
- Closure of the clinic
- Repayment of all fraudulent payments

WASTE:
Waste is the unnecessary consumption of federal resources. It's usually not intentional, but it costs taxpayers money.

Examples of waste:
- Ordering more supplies than needed and having them expire
- Scheduling unnecessary appointments
- Duplicating tests or x-rays
- Using expensive treatments when cheaper alternatives exist
- Staff not working efficiently
- Unnecessary administrative overhead

Consequences of waste:
- Audit findings
- Requirement to repay wasted funds
- Loss of federal funding
- Damage to clinic reputation

ABUSE:
Abuse is violating rules or using resources in ways they're not intended, even if not fraudulent.

Examples of abuse:
- Billing for services at inflated rates
- Not following proper procedures
- Poor quality of care
- Not documenting care appropriately
- Violating HIPAA
- Using supplies for personal use

Consequences of abuse:
- Corrective action plans
- Fines
- Loss of federal funding
- Legal action

THE FALSE CLAIMS ACT:
The False Claims Act is the main federal law against fraud. It says:
"Submitting a false claim for payment from the federal government is illegal."

This includes:
- Knowingly submitting false information
- Recklessly disregarding whether information is false
- Avoiding learning the truth (turning a blind eye)

WHO ENFORCES COMPLIANCE?

Office of Inspector General (OIG):
- Part of HHS
- Investigates fraud and abuse in healthcare
- Has authority to fine, exclude providers, and refer cases for prosecution

FBI:
- Investigates healthcare fraud
- Can file criminal charges

Department of Justice:
- Prosecutes healthcare fraud cases
- Can seek criminal penalties and financial restitution

HRSA Compliance:
- Reviews your clinic during site visits
- Addresses compliance issues
- Can suspend or revoke FQHC status

WHY THIS MATTERS FOR YOU:
Federal healthcare fraud is taken very seriously. If your clinic is found to have committed fraud:
- The clinic loses funding
- Leadership may face criminal charges
- You may lose your job
- Your colleagues lose their jobs
- Patients lose access to care

Even if YOU didn't commit the fraud, YOUR CLINIC is liable for the actions of employees. That's why compliance is everyone's responsibility.

YOUR ROLE:
- Be honest in everything you do
- Document accurately
- Follow all procedures and policies
- Report any suspicious activity immediately
- Ask questions if something doesn't seem right
- Don't participate in any fraudulent activity
- Protect yourself by doing your job correctly`,
        duration: '16 min',
        quiz: [
          {
            question: 'What is the difference between fraud and waste?',
            options: [
              'Fraud is intentional, waste is usually not',
              'Waste costs more money',
              'Fraud only involves billing, waste involves supplies',
              'There is no difference'
            ],
            correct: 0
          },
          {
            question: 'The False Claims Act makes illegal:',
            options: [
              'Only intentional false claims',
              'Only reckless false claims',
              'Both intentional and reckless false claims',
              'Only claims over $1,000'
            ],
            correct: 2
          }
        ]
      },
      {
        id: 'fwa-2',
        title: 'OIG Expectations & Compliance Programs',
        content: `The Office of Inspector General (OIG) publishes guidance on what it expects from healthcare organizations, including FQHCs.

OIG COMPLIANCE PROGRAM GUIDANCE:
OIG recommends that healthcare organizations have a compliance program with these 7 elements:

1. CODE OF CONDUCT
A written code of conduct that describes the organization's commitment to honest, ethical business practices. All staff should review and sign it annually.

Your clinic's code of conduct should cover:
- Commitment to compliance
- Honest billing and coding
- Protecting patient privacy
- Conflict of interest policies
- Accurate documentation
- Reporting concerns
- Consequences of violations

2. COMPLIANCE OFFICER & OVERSIGHT
Designate someone responsible for compliance. This person:
- Oversees the compliance program
- Investigates reported concerns
- Develops training and policies
- Reports to leadership about compliance status
- Works with management to fix problems

Know who your clinic's Compliance Officer is and how to reach them.

3. TRAINING & EDUCATION
All staff must receive training on:
- The clinic's code of conduct
- Billing and coding compliance
- HIPAA and privacy
- False Claims Act and fraud/waste/abuse
- Anti-kickback statutes
- Proper documentation
- How to report concerns

Newer staff get orientation training. All staff get annual training.

4. EFFECTIVE LINES OF COMMUNICATION
Employees must be able to report concerns confidentially without fear of retaliation.

Your clinic should have:
- A compliance hotline (phone number to call)
- Email address for compliance concerns
- A designated person to talk to
- Protection against retaliation
- Documentation of all reports

You should be able to report concerns about:
- Fraudulent billing or claims
- Inappropriate referrals
- Patient safety issues
- Violation of policies
- Conflict of interest
- Falsified documents
- Waste of resources
- Any other compliance concern

5. INTERNAL MONITORING & AUDITING
Your clinic should regularly check itself for compliance problems.

Examples:
- Billing audits (do all claims match actual services?)
- Medical record audits (are all required elements documented?)
- Staff certification audits (do all staff have current licenses?)
- HIPAA audits (is patient information secure?)
- Financial audits (are expenses reasonable and appropriate?)

If problems are found, they should be fixed and reported to leadership.

6. ENFORCEMENT & DISCIPLINE
Staff who violate compliance policies should face consequences.

Examples:
- Verbal warning for minor issues
- Written warning for moderate issues
- Retraining for documentation problems
- Suspension for serious violations
- Termination for fraud or intentional misconduct

Discipline should be applied consistently to all staff.

7. CORRECTIVE ACTION & REMEDIATION
If the clinic identifies a compliance problem, it should:
- Stop the problem immediately
- Investigate the cause
- Fix the process to prevent it from happening again
- Report the problem to appropriate authorities if required
- Reimburse any improper payments
- Document all corrective actions

OIG EXCLUDED PARTIES:
The OIG maintains a list of individuals and organizations that are excluded from participating in federal healthcare programs. This list includes people convicted of fraud, abuse, or other crimes.

Your clinic cannot employ or contract with anyone on the OIG excluded parties list. Before hiring, your clinic should check this list.

COMMON OIG CONCERNS FOR FQHCS:
OIG has identified areas where FQHCs commonly have compliance problems:
- Inaccurate billing and coding
- Improper use of federal funds
- Inadequate documentation
- Falsified credentials
- Kickbacks or improper relationships with referral sources
- Privacy and security violations
- Waste of resources

WHAT YOU SHOULD DO:
- Read your clinic's code of conduct
- Understand the OIG 7 elements
- Participate in compliance training
- Report any suspicious activity
- Ask your Compliance Officer if you have questions
- Document everything accurately
- Follow all policies and procedures
- Do your job with integrity`,
        duration: '18 min',
        quiz: [
          {
            question: 'How many elements should a strong compliance program have according to OIG guidance?',
            options: [
              '3',
              '5',
              '7',
              '10'
            ],
            correct: 2
          },
          {
            question: 'What is the primary purpose of a compliance hotline?',
            options: [
              'To call patients',
              'To allow confidential reporting of compliance concerns',
              'To schedule staff meetings',
              'To report patient complaints'
            ],
            correct: 1
          }
        ]
      },
      {
        id: 'fwa-3',
        title: 'Anti-Kickback Laws & Your Responsibilities',
        content: `The Anti-Kickback Statute (AKS) is a federal law that prohibits offering, paying, soliciting, or receiving anything of value in exchange for referrals or business involving federal healthcare programs.

WHAT IS A KICKBACK?
A kickback is something of value given to encourage referrals or business.

Examples:
- Cash payments for referrals
- Free or discounted supplies in exchange for referrals
- Free services to a provider in exchange for referrals
- Gifts or entertainment beyond modest amounts
- Waived co-payments or deductibles (in some cases)
- Revenue-sharing arrangements based on referrals
- Consulting fees for sham services

WHY IS THIS ILLEGAL?
Kickbacks can lead to:
- Unnecessary referrals (patients don't get care, they get referred)
- Unnecessary procedures (to increase revenue)
- Higher costs for taxpayers
- Conflicts of interest
- Poor quality of care

WHAT IS LEGAL:
Some relationships are okay:
- Salary from your employer
- Fair value consulting fees for real consulting work
- Legitimate business relationships at fair market value
- Discounts available to all customers (not just referral sources)
- Bona fide employee relationships
- Fair market value office space leases

THE STARK LAW:
Similar to the Anti-Kickback Statute, the Stark Law prohibits physicians from referring patients for services to organizations with which they have a financial interest, unless a specific exception applies.

This protects against:
- Physicians referring to their own businesses
- Physicians referring to businesses owned by colleagues
- Physicians financially benefiting from referrals

WHAT THIS MEANS FOR YOU:
At your FQHC:
- You cannot offer referral bonuses to other providers
- You cannot give free or discounted services to encourage referrals
- You cannot have revenue-sharing arrangements based on referrals
- You cannot waive patient co-pays as a kickback
- Referrals must be based on quality of care and patient needs, not financial incentives

RED FLAGS:
Watch for situations that might involve kickbacks:
- A provider offering you money for referrals
- An organization offering free supplies if you refer to them
- Unusual consulting arrangements that don't involve real work
- Excessive gifts or entertainment from vendors
- Waived fees or discounts not offered to others
- Revenue arrangements tied to patient numbers

YOUR RESPONSIBILITY:
- Understand the Anti-Kickback Statute and Stark Law
- Don't participate in any kickback scheme
- Report suspicious arrangements to your Compliance Officer
- Ask questions if something doesn't seem right
- Document all business relationships appropriately
- Be cautious about accepting gifts from vendors
- Ensure referrals are based on quality and patient need

IF YOU SUSPECT A KICKBACK:
- Don't ignore it
- Don't participate
- Report it to your Compliance Officer
- Document what you observed
- Protect the confidentiality of your report
- Seek guidance if you're unsure

REMEMBER:
The Anti-Kickback Statute exists to protect patients and taxpayers. Your job is to make referrals based on what's best for the patient, not based on financial incentives.`,
        duration: '15 min',
        quiz: [
          {
            question: 'What is a kickback?',
            options: [
              'A return of unused funds',
              'Something of value given in exchange for referrals',
              'A discount on services',
              'A bonus paid to all employees'
            ],
            correct: 1
          },
          {
            question: 'Referrals should be based on:',
            options: [
              'Financial incentives',
              'Revenue potential',
              'Quality of care and patient need',
              'Convenience'
            ],
            correct: 2
          }
        ]
      },
      {
        id: 'fwa-4',
        title: 'Your Clinic\'s Code of Conduct & Reporting Concerns',
        content: `Your FQHC should have a written Code of Conduct that describes the organization's commitment to ethical, honest business practices. You should have read it or will read it during orientation.

A strong Code of Conduct covers:

COMMITMENT TO COMPLIANCE
The clinic commits to:
- Following all federal, state, and local laws
- Using federal funds appropriately
- Honest and accurate billing
- Protecting patient privacy
- Providing quality care
- Treating patients and staff with respect
- Reporting and addressing violations

HONEST BILLING & CODING
- Bill only for services actually provided
- Code accurately (don't upcod or downcode)
- Don't submit duplicate claims
- Don't bill for cancelled or no-show appointments
- Only bill insurers for what's appropriate
- Apply sliding fees correctly
- Don't waive co-pays inappropriately

CONFLICT OF INTEREST
Staff must disclose:
- Financial interests in vendors
- Referral arrangements
- Personal relationships with staff or patients that might affect objectivity
- Ownership interests in other organizations
- Any situation where personal interest might conflict with clinic interests

DOCUMENTATION
- Document all patient visits accurately
- Include dates, times, and signatures
- Describe care provided in detail
- Be truthful and complete
- Don't falsify records
- Don't document services not provided
- Don't alter records after the fact

PRIVACY & CONFIDENTIALITY
- Protect patient information
- Don't discuss patients in public
- Don't access medical records you don't need
- Don't share information with unauthorized people
- Report privacy breaches immediately
- Follow all HIPAA requirements

APPROPRIATE USE OF CLINIC RESOURCES
- Use supplies and equipment only for clinic purposes
- Don't steal or misappropriate clinic property
- Don't use clinic technology for personal use
- Report waste to management
- Keep clinic facilities clean and safe
- Use supplies efficiently

RESPECTFUL WORKPLACE
- Treat colleagues with respect
- Don't discriminate or harass
- Report misconduct
- Follow attendance and punctuality policies
- Dress professionally (if your clinic has dress code)
- Maintain a safe, healthy work environment

REPORTING CONCERNS
Your Code of Conduct should state:
- How to report violations (phone, email, in person)
- That reports can be confidential and/or anonymous
- That you are protected from retaliation
- Who investigates reports
- How the clinic follows up

Your clinic should have:
- A Compliance Officer you can contact
- A compliance hotline (anonymous phone line)
- An email address for compliance concerns
- A clear policy against retaliation

HOW TO REPORT:
If you observe or suspect a compliance violation:
1. Don't ignore it
2. Document what you observed (date, time, what happened)
3. Report to your Compliance Officer or use the hotline
4. Be specific and factual
5. Provide any evidence (if you have it)
6. Keep your report confidential
7. Don't take matters into your own hands
8. Don't retaliate against anyone

WHAT COUNTS AS A REPORTABLE CONCERN?
- Fraudulent billing or claims
- Patient safety issues
- Falsified documents or credentials
- Improper financial transactions
- Violation of clinic policies
- Inappropriate referral arrangements
- Privacy or security breaches
- Waste of resources
- Discrimination or harassment
- Any violation of the Code of Conduct

PROTECTION AGAINST RETALIATION:
Federal law protects you from retaliation if you:
- Report a compliance concern in good faith
- Refuse to participate in unlawful activity
- Cooperate with a compliance investigation
- Testify in a compliance proceeding

Retaliation includes:
- Termination
- Demotion
- Adverse shift assignments
- Negative performance review
- Harassment
- Any adverse employment action

If you experience retaliation, report it immediately to your manager or Compliance Officer.

YOUR RESPONSIBILITY:
- Read your clinic's Code of Conduct
- Ask questions if you don't understand
- Follow the Code in all your work
- Report violations you become aware of
- Don't retaliate against others for reporting
- Be honest and ethical in everything you do
- Protect yourself by doing the right thing

REMEMBER:
Your clinic's Code of Conduct exists to protect:
- Federal taxpayers
- Your patients
- Your clinic
- You and your colleagues

When you follow the Code and report violations, you're protecting all of these.`,
        duration: '16 min',
        quiz: [
          {
            question: 'You should report compliance concerns to:',
            options: [
              'Your friends',
              'Patients',
              'Your Compliance Officer or compliance hotline',
              'The news media'
            ],
            correct: 2
          },
          {
            question: 'Your clinic is required to protect you from retaliation if you:',
            options: [
              'Report a compliance concern in good faith',
              'Refuse to participate in unlawful activity',
              'Cooperate with an investigation',
              'All of the above'
            ],
            correct: 3
          }
        ]
      }
    ]
  },
  {
    id: 'osha',
    title: 'OSHA for Healthcare',
    description: 'Bloodborne pathogens, hazard communication, exposure control, and annual healthcare requirements. Keep yourself and your patients safe.',
    duration: '1h 5m',
    lessons: [
      {
        id: 'osh-1',
        title: 'What is OSHA and Why It Matters',
        content: `OSHA = Occupational Safety and Health Administration. It's a federal agency that sets and enforces workplace safety standards.

OSHA'S MISSION:
"To assure safe and healthful working conditions by setting and enforcing standards and by providing training, outreach, and education."

WHY DOES OSHA APPLY TO YOUR FQHC?
Your clinic is a workplace. OSHA regulations protect you and your colleagues from occupational hazards.

Healthcare is a high-risk industry because healthcare workers are exposed to:
- Blood and body fluids
- Needles and sharp instruments
- Infectious diseases
- Chemicals and hazardous substances
- Ergonomic hazards (lifting, repetitive motion)
- Workplace violence and assaults

OSHA REGULATIONS FOR HEALTHCARE:
OSHA has specific standards for healthcare organizations, including:
- Bloodborne Pathogens Standard
- Hazard Communication Standard
- Walking/Working Surfaces
- Fire Safety
- Electrical Safety
- Emergency Action Plans

The most important for your clinic:
- Bloodborne Pathogens Standard

WHO ENFORCES OSHA?
- OSHA inspectors can conduct workplace inspections
- They can issue citations for violations
- They can assess fines for non-compliance
- They investigate complaints from employees

OSHA PROTECTIONS FOR YOU:
- The right to a safe workplace
- The right to know about hazards
- The right to report safety concerns without retaliation
- The right to inspect records of injuries and illnesses
- The right to participate in safety activities

YOUR RESPONSIBILITY:
- Follow all safety procedures
- Use protective equipment as required
- Report hazards and unsafe conditions
- Report injuries and exposures immediately
- Cooperate with OSHA investigations
- Participate in safety training
- Take care of yourself and your colleagues

BLOODBORNE PATHOGENS STANDARD:
This is the main OSHA requirement for healthcare. It protects against exposure to blood and body fluids that might contain HIV, Hepatitis B, Hepatitis C, and other bloodborne pathogens.

Key requirements:
- Exposure control plan
- Training for all potentially exposed staff
- Protective equipment and engineering controls
- Safe handling and disposal of sharps
- Post-exposure evaluation and follow-up
- Vaccination for Hepatitis B (if eligible)
- Recordkeeping

This standard applies if any staff member might be exposed to blood or other potentially infectious materials (OPIM) in their job.

HAZARD COMMUNICATION STANDARD:
This standard requires that you be informed about all hazardous chemicals in your workplace.

Requirements:
- Safety Data Sheets (SDS) for all chemicals
- Labels on all chemical containers
- Training for staff who use chemicals
- Emergency procedures

WHY THIS MATTERS FOR YOU:
OSHA violations put you at risk of:
- Needlestick injuries and bloodborne pathogen exposure
- Chemical burns or exposure
- Ergonomic injuries
- Workplace violence
- Fire and emergency situations

Compliance protects you by:
- Preventing injuries
- Providing safety equipment
- Ensuring proper training
- Creating emergency procedures
- Allowing you to report hazards

IF YOU'RE INJURED AT WORK:
- Report it immediately to your manager
- Seek medical attention if needed
- Document what happened
- Follow your clinic's injury reporting process
- Don't be afraid to report — it's your right

REMEMBER:
You have the right to a safe workplace. OSHA exists to protect you. Your clinic has a responsibility to follow OSHA standards, and you have a responsibility to work safely and report hazards.`,
        duration: '16 min',
        quiz: [
          {
            question: 'OSHA stands for:',
            options: [
              'Organization for Safety and Health Administration',
              'Occupational Safety and Health Administration',
              'Office of Safety and Healthcare Administration',
              'Occupational Service and Healthcare Agency'
            ],
            correct: 1
          },
          {
            question: 'The Bloodborne Pathogens Standard applies to:',
            options: [
              'Only doctors',
              'Only nurses',
              'Staff who might be exposed to blood or body fluids',
              'All employees'
            ],
            correct: 2
          }
        ]
      },
      {
        id: 'osh-2',
        title: 'Bloodborne Pathogens & Exposure Control',
        content: `The Bloodborne Pathogens Standard is OSHA's main requirement for healthcare settings. It protects you from exposure to diseases like HIV, Hepatitis B, and Hepatitis C.

WHAT ARE BLOODBORNE PATHOGENS?
Bloodborne pathogens are disease-causing microorganisms in blood and body fluids.

Main ones of concern:
- HIV (causes AIDS)
- Hepatitis B virus (HBV)
- Hepatitis C virus (HCV)

These can be transmitted through:
- Needlestick injuries
- Cuts with contaminated instruments
- Splashes to eyes, nose, mouth
- Contact with non-intact skin
- Exposure to other potentially infectious materials (saliva in dentistry, semen, vaginal secretions, cerebrospinal fluid, etc.)

WHO IS AT RISK?
Any staff member whose job involves:
- Direct patient care
- Handling needles or sharps
- Contact with blood or body fluids
- Cleaning or disinfecting equipment

This includes:
- Clinical staff (MA, RN, PA, MD)
- Dental staff
- Lab staff
- Housekeeping (exposure to contaminated surfaces)
- Maintenance staff (exposure to contaminated materials)

EXPOSURE CONTROL PLAN:
Your clinic must have a written Exposure Control Plan that:
- Identifies jobs at risk
- Describes how staff will be protected
- Lists all safety procedures
- Describes personal protective equipment (PPE)
- Explains sharps safety
- Details post-exposure procedures
- Documents all training

Your clinic should provide you with a copy of this plan.

PERSONAL PROTECTIVE EQUIPMENT (PPE):
Your clinic must provide PPE at no cost:
- Gloves
- Gowns or aprons
- Eye protection (goggles, face shields)
- Masks
- Respirators (if needed for airborne pathogens)

When to use PPE:
- Whenever you might contact blood or body fluids
- When handling potentially contaminated equipment
- During any procedure with potential for splashing
- During patient transport of potentially infected patients

PPE must be:
- Provided by the clinic (not your responsibility to buy)
- Available when needed
- In appropriate sizes
- Properly stored
- Replaced when damaged

PROPER USE OF PPE:
- Wear gloves whenever you handle blood or body fluids
- Change gloves between patients
- Don't reuse gloves
- Perform hand hygiene after removing gloves
- Wear eye protection if splashing is possible
- Wear a gown if clothing might be splashed
- Use masks appropriately based on situation

SHARPS SAFETY:
Needlestick injuries are the most common occupational injury in healthcare. Your clinic must:
- Use safety-engineered sharps (sharps with built-in safety features)
- Provide sharps containers (puncture-resistant, labeled, closable)
- Never recap needles by hand
- Never bend or manipulate sharps
- Dispose of sharps immediately after use
- Report needlestick injuries immediately

If you have a needlestick injury:
1. Wash the wound with soap and water
2. Report it immediately to your manager or occupational health
3. Seek medical evaluation
4. Get baseline blood test
5. Follow up testing protocol
6. Document the incident
7. Follow post-exposure prophylaxis (PEP) if appropriate

SAFE WORK PRACTICES:
- Perform hand hygiene before and after patient contact
- Clean and disinfect surfaces and equipment
- Handle specimens and contaminated items carefully
- Never eat, drink, or apply cosmetics in areas with exposure risk
- Never store food in refrigerators with blood or body fluids
- Don't mouth-pipette (suck by mouth)
- Avoid contact with contaminated surfaces
- Follow your clinic's procedures for spills

HEPATITIS B VACCINATION:
- OSHA requires that Hepatitis B vaccine be offered to all at-risk employees
- It's free
- It's highly effective (prevents infection)
- You have the right to refuse but must sign a form
- If you refuse and later are exposed, you have limited options
- Most healthcare workers should get it

POST-EXPOSURE EVALUATION:
If you're exposed to blood or body fluids:
1. Immediate medical evaluation
2. Baseline blood test (your blood tested for diseases)
3. Source patient testing (with consent, the patient's blood tested)
4. Follow-up testing (usually 6 weeks, 3 months, 6 months)
5. Possible post-exposure prophylaxis (PEP) treatment
6. Counseling and support

Post-exposure prophylaxis (PEP):
- HIV: Antiretroviral drugs started within 72 hours of exposure
- Hepatitis B: HBIG (Hepatitis B immune globulin) and/or vaccine
- Hepatitis C: Monitoring for infection

DOCUMENTING EXPOSURE:
Your clinic must keep records of:
- All exposure incidents
- Circumstances of the exposure
- Sharps injuries
- Medical follow-up
- Results of testing
- Any PEP treatment given

This information is confidential and protected.

TRAINING:
All at-risk staff must receive training on:
- Routes of transmission
- Risk factors
- How to prevent exposure
- What to do if exposed
- How to use PPE
- Sharps safety
- Bloodborne pathogen standard requirements

You should receive initial training and annual refresher training.

YOUR RESPONSIBILITY:
- Understand bloodborne pathogen risks
- Use PPE appropriately
- Follow safe work practices
- Report hazards and unsafe conditions
- Get Hepatitis B vaccine
- Report exposures immediately
- Follow post-exposure procedures
- Participate in training

REMEMBER:
Bloodborne pathogen exposure is preventable. When you follow safety procedures, wear PPE, and report hazards, you protect yourself, your colleagues, and your patients.`,
        duration: '22 min',
        quiz: [
          {
            question: 'Which of the following is a bloodborne pathogen?',
            options: [
              'Influenza virus',
              'Hepatitis B virus',
              'Measles virus',
              'Common cold virus'
            ],
            correct: 1
          },
          {
            question: 'If you have a needlestick injury, you should:',
            options: [
              'Ignore it unless it causes pain',
              'Report it immediately to your manager',
              'Wait to see if symptoms develop',
              'Tell a colleague but not management'
            ],
            correct: 1
          }
        ]
      },
      {
        id: 'osh-3',
        title: 'OSHA Hazard Communication & Safe Practices',
        content: `The Hazard Communication Standard (HCS) is an OSHA requirement that you be informed about all chemicals in your workplace.

CHEMICALS IN YOUR CLINIC:
Healthcare facilities use many chemicals:
- Disinfectants (bleach, quaternary ammonium compounds, etc.)
- Sterilization chemicals (glutaraldehyde, peracetic acid)
- Laboratory chemicals
- Cleaning products
- Medications
- Anesthetics
- Formaldehyde (in pathology labs)
- Pesticides
- Latex (if used)

All of these can be hazardous if not handled properly.

HAZARD TYPES:
Chemicals can be hazardous in different ways:
- Health hazards (toxic, carcinogenic, cause skin/eye irritation)
- Physical hazards (flammable, explosive, oxidizer)
- Environmental hazards (water pollutant, ozone depleter)

SAFETY DATA SHEETS (SDS):
Every chemical your clinic uses must have an SDS — a document that describes:
- Chemical name and manufacturer
- Hazards (what can happen if exposed)
- Physical properties (appearance, smell, etc.)
- Routes of exposure (inhalation, skin contact, etc.)
- Effects of exposure (acute and chronic)
- First aid measures
- Storage and handling
- Personal protective equipment needed
- Emergency procedures

Your clinic must:
- Have SDS for all chemicals
- Make SDS accessible to all staff
- Provide SDS to you upon request
- Keep SDS organized and updated

You should:
- Know where to find the SDS
- Read SDS before using a new chemical
- Understand the hazards
- Know proper handling and storage
- Know first aid if exposed

CHEMICAL LABELS:
All chemical containers must be labeled with:
- Product name
- Hazard pictogram (warning symbol)
- Hazard statement (what can happen)
- Precautionary statement (how to prevent exposure)
- Manufacturer name and contact info

Always read labels before using a chemical.

HAZARD COMMUNICATION TRAINING:
All staff who work with chemicals must receive training on:
- Types of hazards
- How to read labels
- How to read SDS
- How to use chemicals safely
- How to report spills or exposures
- First aid for exposures
- Emergency procedures

COMMON CHEMICALS IN HEALTHCARE:

Disinfectants (Bleach, Quaternary Ammonium):
- Hazards: Skin/eye irritation, respiratory irritation, toxic
- Proper use: Dilute as directed, use in ventilated area, wear gloves
- Storage: In labeled containers, away from heat/light
- Exposure: Wash with water, seek medical attention if severe

Glutaraldehyde:
- Hazard: Respiratory irritant, skin sensitizer, toxic
- Proper use: Use in fume hood only, wear gloves/apron/eye protection
- Storage: In labeled container, ventilated area
- Exposure: Move to fresh air, wash skin, seek medical attention

Formaldehyde:
- Hazard: Respiratory irritant, carcinogenic, skin irritant
- Proper use: Use in ventilated area only, wear gloves/eye protection
- Storage: In sealed containers
- Exposure: Move to fresh air, wash skin, seek medical attention

SPILL PROCEDURES:
If a chemical spills:
1. Alert people in the area (they should leave if hazardous)
2. Don't panic
3. For small spills: Use spill kit appropriate for the chemical
4. For large spills: Evacuate area, call for help
5. Clean up only if trained and safe to do so
6. Dispose of spilled material in appropriate container
7. Report the spill to your manager
8. Document the incident

PERSONAL PROTECTIVE EQUIPMENT FOR CHEMICALS:
Depending on the chemical:
- Gloves (latex, nitrile, or vinyl depending on chemical)
- Eye protection (goggles or face shield)
- Lab coat or apron
- Respiratory protection (mask or respirator)
- Ventilation (fume hood)

The SDS tells you what PPE is needed.

ERGONOMICS:
While not a specific chemical hazard, OSHA is concerned about:
- Repetitive motion injuries (from typing, clicking mouse)
- Lifting injuries (improper lifting technique)
- Back strain (from prolonged standing or hunching)

Prevent ergonomic injuries:
- Use proper lifting technique (bend knees, not back)
- Use mechanical lifts when needed
- Take breaks from repetitive tasks
- Maintain good posture
- Adjust workstation height
- Report aches and pains

FIRST AID FOR CHEMICAL EXPOSURE:

Skin exposure:
- Remove contaminated clothing
- Flush with water for at least 15 minutes
- Wash with soap and water
- Seek medical attention if persistent irritation

Eye exposure:
- Immediately flush with water for at least 15 minutes
- Use eyewash station if available
- Seek medical attention immediately

Inhalation:
- Move to fresh air immediately
- Loosen tight clothing
- If not breathing, perform CPR
- Seek medical attention

Ingestion (swallowing):
- Call Poison Control: 1-800-222-1222
- Follow their instructions
- Have SDS available to tell Poison Control what was swallowed
- Seek emergency medical attention

YOUR RESPONSIBILITY:
- Know what chemicals you work with
- Read labels and SDS
- Follow proper handling procedures
- Use required PPE
- Report spills or exposures
- Report hazards to your manager
- Participate in training
- Take care of yourself and colleagues

REMEMBER:
Most chemicals are safe when used properly. When you follow procedures, use PPE, and report hazards, you protect yourself and others.`,
        duration: '20 min',
        quiz: [
          {
            question: 'What is an SDS?',
            options: [
              'A type of disinfectant',
              'Safety Data Sheet describing chemical hazards',
              'A personal protective equipment',
              'An OSHA inspection form'
            ],
            correct: 1
          },
          {
            question: 'If you have a chemical spill, you should:',
            options: [
              'Clean it up yourself immediately',
              'Alert people in the area and follow spill procedures',
              'Ignore it if it\'s small',
              'Tell a colleague and leave'
            ],
            correct: 1
          }
        ]
      },
      {
        id: 'osh-4',
        title: 'OSHA Recordkeeping & Your Safety Rights',
        content: `OSHA requires your clinic to keep records of workplace injuries and illnesses. These records help identify hazards and protect workers.

RECORDING REQUIREMENTS:
Your clinic must record:
- All work-related injuries
- All work-related illnesses
- Needlestick and sharps injuries
- Bloodborne pathogen exposures
- Occupational diseases (hepatitis, tuberculosis, etc.)
- Serious illnesses or injuries

Not recorded:
- Minor injuries treated with first aid only (unless it's a needlestick)
- Off-the-job injuries
- Non-work-related illnesses

WORK-RELATED:
An injury or illness is "work-related" if it:
- Results from conditions at the workplace
- Occurs during work activities
- Results from activities required by the job

Examples:
- Needlestick injury while drawing blood = RECORDABLE
- Slip and fall in clinic hallway = RECORDABLE
- Exposure to bloodborne pathogen = RECORDABLE
- Flu caught from a patient = RECORDABLE
- Injury from physical assault by patient = RECORDABLE

OSHA 300 LOG:
Your clinic maintains a log (form OSHA 300) that records:
- Date of injury/illness
- Employee name and job
- Description of what happened
- What injury/illness resulted
- Days away from work
- Job restrictions
- Whether employee was hospitalized

This log is:
- Maintained by the clinic
- Open for employee review
- Kept for 5 years
- Not kept confidential (you can see it)

INCIDENT REPORTING:
If you're injured or become ill at work:
1. Report it immediately to your manager
2. Seek medical attention if needed
3. Document what happened
4. Provide details (date, time, circumstances)
5. Complete any incident report forms
6. The incident will be recorded on OSHA 300 Log

You have the right to:
- Report the incident without fear of retaliation
- Access the OSHA 300 Log
- File a confidential complaint with OSHA if your employer doesn't record it

YOUR OSHA RIGHTS:
You have the right to:

1. A safe workplace
- Free from recognized hazards
- Proper safety equipment
- Training on hazards
- Safe working conditions

2. Know about hazards
- Right to be trained about hazards
- Right to access SDS and safety information
- Right to know about exposures

3. Report unsafe conditions
- Without fear of retaliation
- Confidentially if desired
- To your manager, Safety Officer, or OSHA

4. Request an OSHA inspection
- If you believe there's a hazard
- Confidentially if desired
- Your name not disclosed to employer (if requested)

5. Participate in safety activities
- Safety committees
- Hazard assessments
- Incident investigations
- Training programs

6. Access injury/illness records
- You can view the OSHA 300 Log
- You can get copies of your own records
- Information is not private

7. Protection from retaliation
- You cannot be punished for reporting hazards
- You cannot be punished for refusing unsafe work
- You cannot be punished for reporting an injury
- Reporting protection extends to lawful refusals to work in unsafe conditions

RETALIATION:
It's illegal for your employer to:
- Fire you for reporting a hazard
- Demote you for filing a complaint with OSHA
- Cut your pay for participating in safety activities
- Reduce your hours for reporting an injury
- Harass you for refusing unsafe work

If you experience retaliation:
- Document what happened
- Save emails and text messages
- Report to your manager or HR
- Contact OSHA
- Consult an attorney

OSHA COMPLAINTS:
You can file a complaint with OSHA if:
- Your employer violates OSHA standards
- Working conditions are unsafe
- You're retaliated against for reporting

To file a complaint:
- Contact your local OSHA office
- Describe the hazard or violation
- Provide specific details
- You can file anonymously
- OSHA investigates if there's cause

WORKPLACE VIOLENCE:
OSHA is concerned about violence in healthcare settings:
- Assaults on staff
- Threats
- Verbal abuse
- Property damage

Your clinic should have:
- A workplace violence prevention program
- Training for all staff
- Procedures for reporting incidents
- Support for injured staff
- Security measures as needed

If you're assaulted or threatened:
- Report immediately
- Seek medical attention
- Document details
- File an incident report
- Request support or counseling
- Know you're protected

EMERGENCY ACTION PLAN:
Your clinic must have a plan for:
- Fire evacuation
- Medical emergencies
- Hazardous spills
- Workplace violence
- Natural disasters
- Other emergencies

You should:
- Know the plan
- Know your role
- Know evacuation routes
- Know where to gather
- Participate in drills

YOUR RESPONSIBILITY:
- Know your OSHA rights
- Report hazards and unsafe conditions
- Follow safety procedures
- Use PPE
- Report injuries immediately
- Participate in safety activities
- Protect yourself and colleagues
- Don't retaliate against others for reporting

REMEMBER:
OSHA exists to protect you. Your clinic has a legal obligation to provide a safe workplace, and you have the right to report hazards without fear of retaliation. When you speak up about safety, you're protecting everyone.`,
        duration: '18 min',
        quiz: [
          {
            question: 'Your clinic must record all work-related:',
            options: [
              'Minor injuries',
              'Injuries and illnesses',
              'Patient incidents',
              'Near-misses only'
            ],
            correct: 1
          },
          {
            question: 'You have the right to file an OSHA complaint:',
            options: [
              'Only if your employer agrees',
              'Only if injuries are serious',
              'Confidentially without retaliation',
              'Only after getting legal advice'
            ],
            correct: 2
          }
        ]
      }
    ]
  }
];
